<template>
  <div>
    <div v-if="errorMessage" class="error">
      {{ errorMessage }}
    </div>
    <button
      type="button"
      class="btn btn-primary"
      :class="{ disabled: submitting }"
      @click.prevent="validateAddress()"
    >
      {{ buttonText }}
    </button>
    <inline-loading-indicator v-if="submitting"></inline-loading-indicator>

    <!-- this is in the DOM, but we're pulling it out into the SWAL. This way we get the value of Vue while using the Swal.html stuff -->
    <div class="hidden">
      <div ref="validatedAddresses">
        <ul>
          <li v-for="(validAddress, index) in validatedAddresses" :key="index">
            <button
              class="btn"
              :class="{
                'btn-primary': index === 0,
                'btn-secondary': index !== 0,
              }"
              @click.prevent="pickAddress(validAddress)"
            >
              {{ validAddress.address }} {{ validAddress.address2 }}
              <br />
              {{ validAddress.city }}, {{ validAddress.state }}
              {{ validAddress.county }}
              {{ validAddress.zip }}
              {{ validAddress.country }}
            </button>
          </li>
          <li v-if="validatedAddresses && validatedAddresses.length === 0">
            No matching address could be found
          </li>
        </ul>
        <a href="#" @click.prevent="selfCertifyAddress()">
          Keep my original address. I certify it is correct.
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    address: {
      type: Object,
      required: true,
    },
    buttonText: {
      type: String,
      default: "Validate Address",
    },
  },

  data() {
    return {
      validatedAddresses: [],
      errorMessage: null,
      submitting: false,
    };
  },

  computed: {
    addressToValidate() {
      return {
        address: this.address.address,
        address2: this.address.address2,
        city: this.address.city,
        state: this.address.state,
        county: this.address.county,
        zip: this.address.zip,
        country: this.address.country,
      };
    },
  },

  methods: {
    updateAddressWithValidatedAddress(validAddress) {
      Object.keys(this.addressToValidate).forEach((key) => {
        this.address[key] =
          typeof validAddress[key] !== "undefined" ? validAddress[key] : "";
      });
    },

    checkRequiredFields() {
      return [
        "firstName",
        "lastName",
        "address",
        "city",
        "state",
        "zip",
      ].filter((field) => !this.address[field]);
    },

    validateAddress() {
      this.validatedAddresses = [];
      this.errorMessage = null;

      this.$emit("missing-fields", []);
      const missingFields = this.checkRequiredFields();
      if (missingFields.length > 0) {
        this.$emit("missing-fields", missingFields);
        return;
      }

      this.submitting = true;

      this.$http
        .post("/address/validate-accuracy", this.addressToValidate)
        .then((response) => {
          this.validatedAddresses = response.data;

          this.$swal.fire({
            title: "Select a validated address",
            html: this.$refs.validatedAddresses,
            showCancelButton: true,
            showConfirmButton: false,
            width: "45rem",
          });
        })
        .catch((e) => {
          if (e.response.status !== 422) {
            this.errorMessage =
              "We are temporarily unable to validate your address. You may proceed without validation.";
            this.validatedAddress = [];
          } else {
            const errors = Object.values(e.response.data.errors)
              .flat()
              .join(" ");
            this.errorMessage = `Unable to validate address: ${errors}`;
          }
        })
        .finally(() => {
          this.submitting = false;
        });
    },

    pickAddress(validAddress) {
      this.errorMessage = null;

      this.updateAddressWithValidatedAddress(validAddress);

      this.address.validationType = "smarty-streets";
      this.address.validatedAddress = JSON.stringify(this.addressToValidate);

      this.$swal.close();
      this.$emit("validated");
    },

    selfCertifyAddress() {
      this.errorMessage = null;

      this.address.validationType = "self-certified";
      this.address.validatedAddress = null;

      this.$swal.close();
      this.$emit("validated");
    },
  },
};
</script>

<style scoped>
button {
  white-space: normal;
}

ul {
  list-style: none;
  padding-inline-start: 0px;
}

li {
  padding-bottom: 1rem;
}
</style>
